// src/app/services/category/category.service.ts
import { Injectable, inject } from '@angular/core';
import { Observable, tap, BehaviorSubject, map } from 'rxjs';
import { UtilityService } from '../utility/utility.service';
import { API_CONFIG } from '../../config/api.config';
import { Category, CategoryMapping, EmojiKey } from '../../models/transaction.model';
import { EMOJI_MAPPINGS } from '../../constants/emoji-mappings';

export interface CreateCategoryRequest {
  name: string;
  emoji: string;
  background_color: string;
  description?: string;
};

export interface UpdateCategoryRequest extends CreateCategoryRequest {
  id: string;
};


interface CreateCategoryResponse {
  id?: string;
  name: string;
  emoji: EmojiKey;
  background_color: string;
  created_at: string;
  updated_at: string;
  user_id?: string;
  description?: string;
}

interface ApiCategory {
  id: string;
  name: string;
  description?: string;
  emoji?: string | null;
  background_color?: string | null;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
  is_modificable?: boolean;
}

/**
 * Service to handle category operations, including fetching, creating, updating, and mapping styles.
 */
@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  private _utilityService = inject(UtilityService);
  private _categories = new BehaviorSubject<Category[]>([]);
  categories$ = this._categories.asObservable();

  private get baseUrl(): string {
    return `${API_CONFIG.baseUrl}${API_CONFIG.modules.categories.base}`;
  }

  private _categoryMappings: { [key: string]: CategoryMapping } = {};

  constructor() {
    this._loadCategories();
  }

  /**
   * Updates local category mappings cache from list.
   */
  private updateCategoryMappingsFrom(cats: Category[]): void {
    this._categoryMappings = cats.reduce((acc, c) => {
      acc[c.name.toLowerCase()] = {
        emoji: this._getEmojiFromKey(c.emoji),
        backgroundColor: c.background_color
      };
      return acc;
    }, {} as { [k: string]: CategoryMapping });
  }

  /**
   * Forces a reload of categories from the API.
   */
  refreshCategories(): void {
    this._loadCategories();
  }

  /**
   * Loads categories from API and updates state.
   */
  private _loadCategories(): void {
    this._getCategories().subscribe(cats => {
      this.updateCategoryMappingsFrom(cats);
      this._categories.next(cats);
    });
  }

  /**
   * Resolves emoji character from key.
   * @param key Emoji key or direct character.
   */
  private _getEmojiFromKey(key: EmojiKey | string | null): string {
    if (!key) return '📦';
    if (key in EMOJI_MAPPINGS) {
      return EMOJI_MAPPINGS[key as EmojiKey];
    }
    return '📦';
  }

  /**
   * Maps API category to internal model.
   */
  private _mapApiCategoryToCategory(apiCategory: ApiCategory): Category {
    return {
      id: apiCategory.id,
      name: apiCategory.name,
      description: apiCategory.description,
      emoji: (apiCategory.emoji as EmojiKey) || 'box',
      background_color: apiCategory.background_color || 'bg-gray-50',
      user_id: apiCategory.user_id,
      created_at: apiCategory.created_at,
      updated_at: apiCategory.updated_at,
      is_modificable: apiCategory.is_modificable
    };
  }

  /**
   * Fetches categories from API.
   * @returns {Observable<Category[]>} List of categories.
   */
  _getCategories(): Observable<Category[]> {
    const url = `${this.baseUrl}${API_CONFIG.modules.categories.endpoints.list}`;
    return this._utilityService.request<ApiCategory[]>('GET', url, {}, {}, true, { 'x-skip-loader': 'true' }).pipe(
      map(apiCategories => apiCategories.map(cat => this._mapApiCategoryToCategory(cat)))
    );
  }

  /**
   * Gets category options for dropdowns with computed styles.
   * @returns {Observable<any[]>} Mapped category options.
   */
  getCategoryOptions() {
    return this.categories$.pipe(
      map(cats => cats
        .map(c => {
          const mapping = this.getCategoryMapping(c.name);
          return {
            value: c.id,
            name: c.name,
            emoji: mapping.emoji,
            emojikey: c.emoji,
            backgroundColor: mapping.backgroundColor,
            label: `${mapping.emoji} ${c.name}`,
            is_modificable: c.is_modificable
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'es'))
      )
    );
  }

  /**
   * Gets a category by ID.
   * @param {string} id Category ID.
   * @returns {Category | undefined} The category or undefined.
   */
  getCategoryById(id: string): Category | undefined {
    const category = this._categories.value.find(category => category.id === id);
    return category;
  }

  /**
   * Gets a category by Name.
   * @param {string} name Category Name.
   * @returns {Category | undefined} The category or undefined.
   */
  getCategoryByName(name: string): Category | undefined {
    return this._categories.value.find(category =>
      category.name.toLowerCase() === name.toLowerCase()
    );
  }

  /**
   * Gets style mapping for a category name.
   * @param {string} categoryName Name of the category.
   * @returns {CategoryMapping} Emoji and background color.
   */
  getCategoryMapping(categoryName: string): CategoryMapping {
    const normalized = categoryName.toLowerCase();
    return this._categoryMappings[normalized] || { emoji: '📦', backgroundColor: 'bg-gray-50' };
  }

  /**
   * Resolves an emoji character directly from an EMOJI_MAPPINGS key.
   * @param {string} key Emoji key (e.g. "house", "cinema").
   * @returns {string} Emoji character or 📦 if not found.
   */
  getEmojiFromKey(key: string): string {
    return this._getEmojiFromKey(key);
  }

  /**
   * Gets emoji for a category by name or ID.
   * @param {string} categoryNameOrId Name or ID.
   * @returns {string} Emoji character.
   */
  getEmojiForCategory(categoryNameOrId: string): string {
    let category = this.getCategoryByName(categoryNameOrId);
    if (!category) {
      category = this.getCategoryById(categoryNameOrId);
    }

    if (category) {
      return this._getEmojiFromKey(category.emoji);
    }

    return this.getCategoryMapping(categoryNameOrId).emoji;
  }

  /**
   * Gets background color for a category by name or ID.
   * @param {string} categoryNameOrId Name or ID.
   * @returns {string} Background color class/code.
   */
  getBackgroundColorForCategory(categoryNameOrId: string): string {
    let category = this.getCategoryByName(categoryNameOrId);
    if (!category) {
      category = this.getCategoryById(categoryNameOrId);
    }

    if (category) {
      return category.background_color;
    }

    return this.getCategoryMapping(categoryNameOrId).backgroundColor;
  }

  /**
   * Creates a new category.
   * @param {CreateCategoryRequest} category Data for new category.
   * @returns {Observable<CreateCategoryResponse>} The created category response.
   */
  createCategory(category: CreateCategoryRequest): Observable<CreateCategoryResponse> {
    const url = `${this.baseUrl}${API_CONFIG.modules.categories.endpoints.create}`;
    return this._utilityService.request<CreateCategoryResponse>('POST', url, category).pipe(
      tap(() => this._loadCategories())
    );
  }

  /**
   * Updates an existing category.
   * @param {UpdateCategoryRequest} data Updated data.
   * @returns {Observable<Category>} The updated category.
   */
  updateCategory(data: UpdateCategoryRequest): Observable<Category> {
    const url = `${this.baseUrl}/${data.id}`;
    return this._utilityService.request<ApiCategory>('PUT', url, data).pipe(
      tap(() => this._loadCategories()),
      map(apiCategory => this._mapApiCategoryToCategory(apiCategory))
    );
  }

  /**
   * Deletes a category.
   * @param {string} categoryId Category ID.
   * @returns {Observable<{ message: string }>} Success message.
   */
  deleteCategory(categoryId: string): Observable<{ message: string }> {
    const url = `${this.baseUrl}/${categoryId}`;
    return this._utilityService.request<{ message: string }>('DELETE', url).pipe(
      tap(() => this._loadCategories())
    );
  }
}